import { Component, Input, OnInit, SimpleChanges } from '@angular/core';

import { BsModalRef, BsModalService } from 'ngx-bootstrap/modal';
import { AlertBannerService, AlertType } from 'src/app/lib/alert-banner';
import { OrganisationService } from 'src/app/services/organisation/organisation.service';
import { PermissionsService } from 'src/app/services/permissions/permissions.service';
import {
  AiSettingsService,
  AiSettingsConfig,
  AiSettingsResponse,
} from 'src/app/services/ai-settings/ai-settings.service';
import * as _ from 'lodash';
import { WarningModalService } from 'src/app/services/warning-modal/warning-modal.service';

@Component({
  selector: 'app-ai-settings',
  templateUrl: './ai-settings.component.pug',
  styleUrls: ['./ai-settings.component.scss'],
  standalone: false,
})
export class AiSettingsComponent implements OnInit {
  @Input() org: any;

  aiSettings: AiSettingsResponse | null = null;
  isLoading = false;
  isUpdating = false;
  canToggleAISupportAndReferenceSummary = false;
  canToggleAISupportAndPulseSummary = false;

  constructor(
    public bsModalRef: BsModalRef,
    public permissions: PermissionsService,
    private aiSettingsService: AiSettingsService,
    private alertBannerService: AlertBannerService,
  ) {}

  ngOnInit(): void {
    this.loadAiSettings();
    this.canToggleAISupportAndReferenceSummary = this.permissions.hasPermission('can_set_ai_settings');
    this.canToggleAISupportAndPulseSummary = this.permissions.hasPermission('can_set_pulse_ai_settings');
  }

  /**
   * Load AI settings from the API
   */
  loadAiSettings(): void {
    this.isLoading = true;
    this.aiSettingsService.getAiSettings().subscribe({
      next: (response: AiSettingsResponse) => {
        this.aiSettings = response;
        this.isLoading = false;
      },
      error: (error) => {
        console.error('Error loading AI settings:', error);
        this.alertBannerService.append([
          {
            type: AlertType.DANGER,
            message: 'Error loading AI settings. Please try again.',
          },
        ]);
        this.isLoading = false;
      },
    });
  }

  updateAiSettings(config: AiSettingsConfig, previousConfig: AiSettingsConfig): void {
    if (this.isUpdating) {
      return;
    }

    const configToUpdate: AiSettingsConfig = { ...config };

    // Optimistic update
    if (this.aiSettings) {
      this.aiSettings = {
        ...this.aiSettings,
        config: { ...config },
      };
    }

    this.isUpdating = true;
    this.aiSettingsService.updateAiSettings(configToUpdate).subscribe({
      next: (response: AiSettingsResponse) => {
        this.aiSettings = {
          ...this.aiSettings,
          ...response,
          config: {
            ...this.aiSettings?.config,
            ...response.config,
          },
        };
        this.alertBannerService.append([
          {
            type: AlertType.SUCCESS,
            message: 'AI settings updated successfully.',
          },
        ]);
        this.isUpdating = false;
      },
      error: (error) => {
        console.error('Error updating AI settings:', error);
        // Rollback (optimistic update was applied)
        if (this.aiSettings) {
          this.aiSettings = {
            ...this.aiSettings,
            config: { ...previousConfig },
          };
        }
        this.alertBannerService.append([
          {
            type: AlertType.DANGER,
            message: 'Error updating AI settings. Please try again.',
          },
        ]);
        this.isUpdating = false;
      },
    });
  }

  /**
   * Toggle AI support setting
   */
  toggleAiSupport(): void {
    if (!this.aiSettings) return;

    const previousConfig: AiSettingsConfig = { ...this.aiSettings.config };
    const updatedConfig: AiSettingsConfig = {
      ...this.aiSettings.config,
      support: !this.aiSettings.config.support,
    };

    this.updateAiSettings(updatedConfig, previousConfig);
  }

  /**
   * Toggle AI summaries setting
   */
  toggleAiSummaries(): void {
    if (!this.aiSettings) return;

    const previousConfig: AiSettingsConfig = { ...this.aiSettings.config };
    const updatedConfig: AiSettingsConfig = {
      ...this.aiSettings.config,
      summary: !this.aiSettings.config.summary,
    };

    this.updateAiSettings(updatedConfig, previousConfig);
  }

  /**
   * Toggle AI pulse summaries setting
   */
  toggleAiPulseSummaries(): void {
    if (!this.aiSettings) return;

    const previousConfig: AiSettingsConfig = { ...this.aiSettings.config };
    const updatedConfig: AiSettingsConfig = {
      ...this.aiSettings.config,
      pulse_summary: !this.aiSettings.config.pulse_summary,
    };

    this.updateAiSettings(updatedConfig, previousConfig);
  }

  /**
   * Get the current support setting value
   */
  get supportEnabled(): boolean {
    return this.aiSettings?.config?.support ?? false;
  }

  /**
   * Get the current summary setting value
   */
  get summaryEnabled(): boolean {
    return this.aiSettings?.config?.summary ?? false;
  }

  /**
   * Get the current pulse summary setting value
   */
  get pulseSummaryEnabled(): boolean {
    return this.aiSettings?.config?.pulse_summary ?? false;
  }

  /**
   * Check if toggles should be disabled based on user role
   * Returns true if role is not 'owner' or 'support'
   */
  get isToggleDisabled(): boolean {
    const userRole = this.permissions?.data?.['role']?.['role'];
    if (!userRole) {
      return true; // Default to disabled if role cannot be determined
    }
    return userRole !== 'owner' && userRole !== 'support';
  }
}
